import React, { useMemo, useState } from 'react';
import { Box, Typography, Rating, useTheme, alpha } from '@mui/material';
import { 
  LocationOn as LocationIcon, 
  AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon 
} from '@mui/icons-material';
import CategoryDisplay from '../../common/CategoryDisplay';

/**
 * Section Informations - Titre, catégorie, évaluation, localisation
 */
const InfoSection = ({ 
  listing, 
  layout = 'comfortable',
  formatLocation,
  formatTimeAgo,
  t,
  isProduct = false
}) => {
  const theme = useTheme();
  const [showFullAddress, setShowFullAddress] = useState(false);

  const compactLocation = useMemo(() => formatLocation(listing), [formatLocation, listing]);
  const fullAddress = listing.fullAddress || listing.pickup_location?.address || '';
  
  if (layout === 'compact') {
    return (
      <Box sx={{ flex: '0 0 30%', display: 'flex', flexDirection: 'column', p: 1.5, justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {listing.title}
          </Typography>
          
          {/* Affichage de la catégorie */}
          {listing.category && (
            <Box sx={{ mb: 1.5 }}>
              <CategoryDisplay 
                category={listing.category}
                variant="chip"
                size="small"
                maxWidth="150px"
              />
            </Box>
          )}
          
          {/* Évaluation en mode compact */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Rating value={parseFloat(listing.rating_avg || listing.rating || 0) || 0} size="small" readOnly />
            <Typography variant="caption" color="text.secondary">
              ({parseInt(listing.review_count || listing.rating_count || 0, 10) || 0})
            </Typography>
          </Box>


        </Box>
          
        {/* Localisation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              whiteSpace: showFullAddress ? 'normal' : 'nowrap',
              overflow: showFullAddress ? 'visible' : 'hidden',
              textOverflow: showFullAddress ? 'clip' : 'ellipsis',
              wordBreak: showFullAddress ? 'break-word' : 'normal',
              maxWidth: '100%'
            }}
          >
            {showFullAddress && fullAddress ? fullAddress : compactLocation}
          </Typography>
        </Box>

        {isProduct && fullAddress && fullAddress !== compactLocation && (
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={(e) => {
              e.stopPropagation();
              setShowFullAddress((prev) => !prev);
            }}
            sx={{
              mt: 0.5,
              p: 0,
              border: 'none',
              background: 'transparent',
              color: 'primary.main',
              fontWeight: 600,
              textAlign: 'left',
              cursor: 'pointer'
            }}
          >
            {showFullAddress ? t('common.showLess', 'Afficher moins') : t('common.showMore', 'Afficher plus')}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom noWrap>
        {listing.title}
      </Typography>
      
      {/* Affichage de la catégorie */}
      {listing.category && (
        <Box sx={{ mb: 1.5 }}>
          <CategoryDisplay 
            category={listing.category}
            variant="chip"
            size="small"
            maxWidth="180px"
          />
        </Box>
      )}
      
      {/* Description */}
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          mb: 2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}
      >
        {listing.description}
      </Typography>

      {/* Localisation et propriétaire */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {formatLocation(listing)}
          </Typography>
        </Box>
        
        {listing.distance && (
          <Typography variant="caption" color="primary.main" fontWeight={600}>
            {listing.distance} km
          </Typography>
        )}
      </Box>

      {/* Informations temporelles */}
      {listing.created_at && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {formatTimeAgo(listing.created_at)}
          </Typography>
        </Box>
      )}

      {/* Évaluation et métadonnées déplacées en bas pour les modes détaillés */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        p: 1.5,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
      }}>
        <Rating value={parseFloat(listing.rating_avg || 0) || 0} size="small" readOnly />
        <Typography variant="caption" color="text.secondary">
          ({parseInt(listing.review_count || 0, 10)} {t('listing.reviews')})
        </Typography>
        
        {listing.viewCount && (
          <>
            <VisibilityIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {listing.viewCount}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default InfoSection;
